import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Phone, Mail, Building2, MapPin, StickyNote, Ship,
  Edit3, Trash2, Plus, Coins,
} from 'lucide-react';
import ErpShell from '../components/ErpShell';
import { WhatsAppReminder } from '../components/WhatsAppReminder';
import { api } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { fmtMoney, shipmentFinance, PAY_STATUS_TONE, PAYMENT_METHODS, todayStr } from '../lib/financeShared';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const CATEGORIES = [
  { v: 'IMPORT', label: 'Import' },
  { v: 'EXPORT', label: 'Export' },
  { v: 'ADAM_CUSTOMER', label: 'Adam (container/LCL/air)' },
  { v: 'CHINA_IMPORT', label: 'China import' },
];

const emptyShipment = () => ({
  category: 'IMPORT', trackingNumber: '', description: '', shippingCost: '',
  amountRequested: '', month: '', notes: '',
});

export default function ErpCustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [customer, setCustomer] = useState(null);
  const [records, setRecords] = useState([]);
  const [totals, setTotals] = useState({ requested: 0, paid: 0, left: 0, count: 0 });
  const [ledger, setLedger] = useState({ entries: [], totals: { charged: 0, paid: 0, left: 0, count: 0 } });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});

  const [shipOpen, setShipOpen] = useState(false);
  const [shipForm, setShipForm] = useState(emptyShipment());
  const [shipSaving, setShipSaving] = useState(false);

  const [payModal, setPayModal] = useState(null);
  const [payForm, setPayForm] = useState({ amount: '', date: todayStr(), method: 'Cash', note: '' });
  const [payBusy, setPayBusy] = useState(false);

  const [balEdit, setBalEdit] = useState(false);
  const [balForm, setBalForm] = useState({ shouldPay: '', paid: '' });
  const [balSaving, setBalSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, r, l] = await Promise.all([
        api.get(`/customers/${id}`),
        api.get(`/customers/${id}/records`).catch(() => ({ data: { records: [], totals: {} } })),
        api.get(`/customers/${id}/ledger`).catch(() => ({ data: { entries: [], totals: {} } })),
      ]);
      setCustomer(c.data.customer);
      setForm(c.data.customer || {});
      setRecords(r.data.records || []);
      setTotals(r.data.totals || { requested: 0, paid: 0, left: 0, count: 0 });
      setLedger({ entries: l.data.entries || [], totals: l.data.totals || { charged: 0, paid: 0, left: 0, count: 0 } });
    } catch {
      toast({ title: 'Customer not found', variant: 'destructive' });
      navigate('/erp/customers');
    } finally { setLoading(false); }
  }, [id, navigate, toast]);

  useEffect(() => { load(); }, [load]);

  const saveCustomer = async () => {
    try {
      await api.put(`/customers/${id}`, form);
      toast({ title: 'Customer updated' });
      setEditing(false);
      load();
    } catch { toast({ title: 'Failed to update', variant: 'destructive' }); }
  };

  const removeCustomer = async () => {
    if (!window.confirm('Delete this customer?')) return;
    try {
      await api.delete(`/customers/${id}`);
      toast({ title: 'Customer deleted' });
      navigate('/erp/customers');
    } catch { toast({ title: 'Failed to delete', variant: 'destructive' }); }
  };

  const addShipment = async () => {
    setShipSaving(true);
    try {
      await api.post('/records', {
        ...shipForm,
        category: shipForm.category,
        customerId: id,
        customerName: customer.name,
        phone: customer.phone,
      });
      toast({ title: 'Shipment added' });
      setShipOpen(false);
      setShipForm(emptyShipment());
      load();
    } catch { toast({ title: 'Failed to add shipment', variant: 'destructive' }); }
    finally { setShipSaving(false); }
  };

  const removeShipment = async (rid) => {
    if (!window.confirm('Delete this shipment?')) return;
    try { await api.delete(`/documents/${rid}`); toast({ title: 'Shipment deleted' }); load(); }
    catch { toast({ title: 'Failed', variant: 'destructive' }); }
  };

  const openPay = async (r) => {
    setPayForm({ amount: '', date: todayStr(), method: 'Cash', note: '' });
    setPayModal({ record: r, loading: true });
    try {
      const res = await api.get(`/records/${r.id}/payments`);
      setPayModal({ record: r, ...res.data });
    } catch { setPayModal({ record: r, payments: [] }); }
  };
  const addPayment = async () => {
    const amount = parseFloat(payForm.amount);
    if (!amount || amount <= 0) { toast({ title: 'Enter a valid amount', variant: 'destructive' }); return; }
    setPayBusy(true);
    try {
      const res = await api.post(`/records/${payModal.record.id}/payments`, { ...payForm, amount });
      const rec = res.data.record;
      setPayModal({ record: rec, payments: rec.payments || [], amountRequested: rec.amountRequested, amountPaid: rec.amountPaid, balanceLeft: rec.balanceLeft });
      setPayForm({ amount: '', date: todayStr(), method: 'Cash', note: '' });
      load();
      toast({ title: 'Payment added' });
    } catch (e) { toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); }
    finally { setPayBusy(false); }
  };
  const deletePayment = async (pid) => {
    try {
      const res = await api.delete(`/records/${payModal.record.id}/payments/${pid}`);
      const rec = res.data.record;
      setPayModal({ record: rec, payments: rec.payments || [], amountRequested: rec.amountRequested, amountPaid: rec.amountPaid, balanceLeft: rec.balanceLeft });
      load();
    } catch { toast({ title: 'Failed', variant: 'destructive' }); }
  };

  // Account balance: ledger (dated entries) takes priority, then manual, then shipment totals
  const hasLedger = (ledger.entries || []).length > 0;
  const manualOn = !hasLedger && !!customer?.manualBalance;
  const balRequested = hasLedger ? ledger.totals.charged : (manualOn ? Number(customer.manualShouldPay || 0) : totals.requested);
  const balPaid = hasLedger ? ledger.totals.paid : (manualOn ? Number(customer.manualPaid || 0) : totals.paid);
  const balLeft = Math.round((balRequested - balPaid) * 100) / 100;

  const openBalEdit = () => {
    setBalForm({ shouldPay: String(balRequested || ''), paid: String(balPaid || '') });
    setBalEdit(true);
  };
  const saveBalance = async () => {
    setBalSaving(true);
    try {
      await api.put(`/customers/${id}/balance`, {
        manualBalance: true,
        manualShouldPay: parseFloat(balForm.shouldPay) || 0,
        manualPaid: parseFloat(balForm.paid) || 0,
      });
      toast({ title: 'Balance updated' });
      setBalEdit(false);
      load();
    } catch { toast({ title: 'Failed to update balance', variant: 'destructive' }); }
    finally { setBalSaving(false); }
  };
  const useAutoBalance = async () => {
    setBalSaving(true);
    try {
      await api.put(`/customers/${id}/balance`, { manualBalance: false, manualShouldPay: 0, manualPaid: 0 });
      toast({ title: 'Now using shipment totals' });
      setBalEdit(false);
      load();
    } catch { toast({ title: 'Failed', variant: 'destructive' }); }
    finally { setBalSaving(false); }
  };

  // Account ledger (dated charges + payments)
  const [ledgerModal, setLedgerModal] = useState(null); // 'charge' | 'payment'
  const [ledgerForm, setLedgerForm] = useState({ amount: '', date: todayStr(), method: 'Cash', note: '' });
  const [ledgerBusy, setLedgerBusy] = useState(false);

  const openLedger = (type) => { setLedgerForm({ amount: '', date: todayStr(), method: 'Cash', note: '' }); setLedgerModal(type); };
  const saveLedger = async () => {
    const amount = parseFloat(ledgerForm.amount);
    if (!amount || amount <= 0) { toast({ title: 'Enter a valid amount', variant: 'destructive' }); return; }
    setLedgerBusy(true);
    try {
      await api.post(`/customers/${id}/ledger`, {
        type: ledgerModal, amount, date: ledgerForm.date,
        method: ledgerModal === 'payment' ? ledgerForm.method : '', note: ledgerForm.note,
      });
      toast({ title: ledgerModal === 'charge' ? 'Charge added' : 'Payment added' });
      setLedgerModal(null);
      load();
    } catch (e) { toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); }
    finally { setLedgerBusy(false); }
  };
  const deleteLedger = async (entryId) => {
    if (!window.confirm('Delete this entry?')) return;
    try { await api.delete(`/customers/${id}/ledger/${entryId}`); toast({ title: 'Deleted' }); load(); }
    catch { toast({ title: 'Failed', variant: 'destructive' }); }
  };

  if (loading || !customer) {
    return (
      <ErpShell title="Customer">
        <div className="animate-pulse space-y-4"><div className="h-32 gg-card" /><div className="h-48 gg-card" /></div>
      </ErpShell>
    );
  }

  return (
    <ErpShell title={customer.name}>
      <Link to="/erp/customers" className="gg-btn-ghost mb-4 -ms-2" data-testid="back-btn">
        <ArrowLeft size={14} /> All customers
      </Link>

      {/* Header */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="gg-card p-7 relative overflow-hidden mb-6">
        <div className="absolute -top-16 -end-16 w-64 h-64 rounded-full opacity-40 pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(212,175,55,0.5), transparent 70%)', filter: 'blur(40px)' }} />
        <div className="flex items-start justify-between gap-4 flex-wrap relative">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-3xl flex items-center justify-center shrink-0 text-xl font-bold" style={{ background: 'linear-gradient(135deg, #0A1F3C, #16324F)', color: '#E2C15B', boxShadow: '0 8px 24px -6px rgba(10,31,60,0.5)' }} data-testid="customer-avatar">
              {(customer.name || '?').charAt(0).toUpperCase()}
            </div>
            <div>
              <p className="gg-eyebrow" style={{ color: 'var(--gg-accent)' }}>Customer{customer.customerNumber ? ` · #${customer.customerNumber}` : ''}</p>
              <h1 className="gg-hero-title" style={{ fontSize: 32 }}>{customer.name}</h1>
              {customer.company && <p className="text-sm mt-1 flex items-center gap-1" style={{ color: 'var(--gg-text-secondary)' }}><Building2 size={12} /> {customer.company}</p>}
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setEditing((e) => !e)} className="gg-btn-secondary" data-testid="edit-btn"><Edit3 size={13} /> {editing ? 'Cancel' : 'Edit'}</button>
            <button onClick={removeCustomer} className="gg-btn-secondary" data-testid="delete-btn" style={{ color: 'var(--gg-danger)' }}><Trash2 size={13} /> Delete</button>
          </div>
        </div>

        {editing ? (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3">
            {[{ k: 'customerNumber', label: 'Customer No. (6 digits)' }, { k: 'name', label: 'Name' }, { k: 'company', label: 'Company' }, { k: 'phone', label: 'Phone' }, { k: 'email', label: 'Email' }, { k: 'idNumber', label: 'ID Number' }, { k: 'address', label: 'Address' }].map((f) => (
              <div key={f.k}>
                <label className="gg-eyebrow block mb-1">{f.label}</label>
                <input value={form[f.k] || ''} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} className="gg-input" data-testid={`edit-${f.k}`} />
              </div>
            ))}
            <div className="md:col-span-2">
              <label className="gg-eyebrow block mb-1">Notes</label>
              <textarea value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="gg-input" rows={3} style={{ height: 'auto', padding: '10px 14px' }} data-testid="edit-notes" />
            </div>
            <div className="md:col-span-2 flex justify-end"><button onClick={saveCustomer} className="gg-btn-primary" data-testid="save-btn">Save</button></div>
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            {customer.phone && <InfoRow icon={Phone} label="Phone" value={customer.phone} />}
            {customer.email && <InfoRow icon={Mail} label="Email" value={customer.email} />}
            {customer.idNumber && <InfoRow icon={Building2} label="ID" value={customer.idNumber} />}
            {customer.address && <InfoRow icon={MapPin} label="Address" value={customer.address} />}
            {customer.notes && <InfoRow icon={StickyNote} label="Notes" value={customer.notes} />}
          </div>
        )}
      </motion.div>

      {/* Balance summary — editable directly, no shipment needed */}
      <div className="gg-card p-5 mb-6" data-testid="balance-card">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div>
            <p className="gg-eyebrow">Account balance</p>
            <p className="text-xs mt-0.5" style={{ color: 'var(--gg-text-dim)' }}>
              {hasLedger ? 'From account ledger' : manualOn ? 'Manually set' : 'Calculated from shipments'}
            </p>
          </div>
          {hasLedger ? null : !balEdit ? (
            <button onClick={openBalEdit} className="gg-btn-secondary" data-testid="edit-balance-btn"><Edit3 size={13} /> Edit balance</button>
          ) : (
            <div className="flex gap-2">
              {manualOn && <button onClick={useAutoBalance} disabled={balSaving} className="gg-btn-ghost" data-testid="use-auto-balance">Use shipment totals</button>}
              <button onClick={() => setBalEdit(false)} disabled={balSaving} className="gg-btn-secondary">Cancel</button>
              <button onClick={saveBalance} disabled={balSaving} className="gg-btn-primary" data-testid="save-balance-btn">{balSaving ? 'Saving…' : 'Save'}</button>
            </div>
          )}
        </div>

        {balEdit && !hasLedger ? (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="gg-eyebrow block mb-1">Should pay (₪)</label>
              <input type="number" value={balForm.shouldPay} onChange={(e) => setBalForm({ ...balForm, shouldPay: e.target.value })} className="gg-input" data-testid="bal-shouldpay" />
            </div>
            <div>
              <label className="gg-eyebrow block mb-1" style={{ color: 'var(--gg-emerald)' }}>Paid (₪)</label>
              <input type="number" value={balForm.paid} onChange={(e) => setBalForm({ ...balForm, paid: e.target.value })} className="gg-input" data-testid="bal-paid" />
            </div>
            <div>
              <label className="gg-eyebrow block mb-1">Left (auto)</label>
              <div className="gg-input flex items-center gg-tabular font-bold" style={{ color: ((parseFloat(balForm.shouldPay) || 0) - (parseFloat(balForm.paid) || 0)) > 0 ? 'var(--gg-danger)' : 'var(--gg-emerald)' }} data-testid="bal-left-preview">
                {fmtMoney((parseFloat(balForm.shouldPay) || 0) - (parseFloat(balForm.paid) || 0))}
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="rounded-2xl p-4" style={{ background: 'var(--gg-surface-2)' }}>
              <p className="gg-eyebrow">Should pay</p>
              <p className="gg-tabular mt-1" style={{ fontSize: 24, fontWeight: 700 }} data-testid="total-requested">{fmtMoney(balRequested)}</p>
            </div>
            <div className="rounded-2xl p-4" style={{ background: 'var(--gg-emerald-soft)' }}>
              <p className="gg-eyebrow" style={{ color: 'var(--gg-emerald)' }}>Paid</p>
              <p className="gg-tabular mt-1" style={{ fontSize: 24, fontWeight: 700, color: 'var(--gg-emerald)' }} data-testid="total-paid">{fmtMoney(balPaid)}</p>
            </div>
            <div className="rounded-2xl p-4" style={{ background: balLeft > 0 ? 'rgba(255,59,48,0.08)' : 'var(--gg-emerald-soft)' }}>
              <p className="gg-eyebrow" style={{ color: balLeft > 0 ? 'var(--gg-danger)' : 'var(--gg-emerald)' }}>Payment left</p>
              <p className="gg-tabular mt-1" style={{ fontSize: 24, fontWeight: 700, color: balLeft > 0 ? 'var(--gg-danger)' : 'var(--gg-emerald)' }} data-testid="total-left">{fmtMoney(balLeft)}</p>
            </div>
          </div>
        )}
        {manualOn && (
          <p className="text-xs mt-3" style={{ color: 'var(--gg-text-dim)' }} data-testid="shipment-totals-hint">
            From shipments: requested {fmtMoney(totals.requested)} · paid {fmtMoney(totals.paid)}
          </p>
        )}
      </div>

      {/* Account ledger — dated charges & payments */}
      <div className="gg-card p-6 mb-6" data-testid="ledger-card">
        <div className="flex items-baseline justify-between mb-5 flex-wrap gap-3">
          <div>
            <p className="gg-eyebrow">Account ledger</p>
            <h2 className="gg-section-title mt-1" style={{ fontSize: 20 }}>Charges &amp; payments · {ledger.entries.length}</h2>
          </div>
          <div className="flex gap-2">
            <button onClick={() => openLedger('charge')} className="gg-btn-secondary" data-testid="add-charge-btn"><Plus size={14} /> Add charge (I need…)</button>
            <button onClick={() => openLedger('payment')} className="gg-btn-primary" data-testid="add-payment-btn"><Plus size={14} /> Add payment</button>
          </div>
        </div>

        {ledger.entries.length === 0 ? (
          <p className="text-sm text-center py-8" style={{ color: 'var(--gg-text-dim)' }}>
            No entries yet. Add a charge like “I need ₪670 from {customer.name}”, then record each payment with its date and cash/bank.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="gg-table" style={{ minWidth: 720 }}>
              <thead>
                <tr><th>Date</th><th>Type</th><th>Method</th><th>Note</th><th>Amount</th><th>Balance</th><th style={{ width: 50 }}></th></tr>
              </thead>
              <tbody>
                {ledger.entries.map((e) => (
                  <tr key={e.id} data-testid={`ledger-row-${e.id}`}>
                    <td className="gg-tabular" style={{ color: 'var(--gg-text-dim)' }}>{e.date}</td>
                    <td>
                      {e.type === 'charge'
                        ? <span className="gg-chip gg-chip-warning">Charge</span>
                        : <span className="gg-chip gg-chip-emerald">Payment</span>}
                    </td>
                    <td style={{ color: 'var(--gg-text-secondary)' }}>{e.type === 'payment' ? (e.method || '—') : '—'}</td>
                    <td className="max-w-[220px] truncate" style={{ color: 'var(--gg-text-secondary)' }}>{e.note || '—'}</td>
                    <td className="gg-tabular font-semibold" style={{ color: e.type === 'charge' ? 'var(--gg-text)' : 'var(--gg-emerald)' }}>
                      {e.type === 'charge' ? '+' : '−'}{fmtMoney(e.amount)}
                    </td>
                    <td className="gg-tabular font-semibold" style={{ color: e.runningBalance > 0 ? 'var(--gg-danger)' : 'var(--gg-text-dim)' }}>{fmtMoney(e.runningBalance)}</td>
                    <td><button onClick={() => deleteLedger(e.id)} className="gg-btn-ghost" style={{ color: 'var(--gg-danger)' }} data-testid={`del-ledger-${e.id}`}><Trash2 size={12} /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Shipments */}
      <div className="gg-card p-6" data-testid="customer-shipments">
        <div className="flex items-baseline justify-between mb-5 flex-wrap gap-3">
          <div>
            <p className="gg-eyebrow">History</p>
            <h2 className="gg-section-title mt-1" style={{ fontSize: 20 }}>Shipments · {records.length}</h2>
          </div>
          <div className="flex gap-2 items-center">
            {balLeft > 0 && <WhatsAppReminder name={customer.name} phone={customer.phone} remaining={balLeft} />}
            <button onClick={() => { setShipForm(emptyShipment()); setShipOpen(true); }} className="gg-btn-primary" data-testid="add-shipment-btn"><Plus size={14} /> Add Shipment</button>
          </div>
        </div>

        {records.length === 0 ? (
          <p className="text-sm text-center py-10" style={{ color: 'var(--gg-text-dim)' }}>No shipments yet. Click “Add Shipment” to create the first one.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="gg-table" style={{ minWidth: 820 }}>
              <thead>
                <tr>
                  <th>Reference</th><th>Type</th><th>Requested</th><th>Paid</th><th>Left</th><th>Status</th><th style={{ width: 120 }}></th>
                </tr>
              </thead>
              <tbody>
                {records.map((r) => {
                  const fin = shipmentFinance(r);
                  return (
                    <tr key={r.id} data-testid={`shipment-row-${r.id}`}>
                      <td className="font-semibold gg-tabular">{r.trackingNumber || r.fileNumber || r.id.slice(0, 8)}
                        {r.description && <span className="block text-xs font-normal" style={{ color: 'var(--gg-text-dim)' }}>{r.description}</span>}
                      </td>
                      <td><span className="gg-chip gg-chip-neutral">{(CATEGORIES.find((c) => c.v === r.category) || {}).label || r.category}</span></td>
                      <td className="gg-tabular font-semibold">{fmtMoney(fin.requested)}</td>
                      <td className="gg-tabular font-semibold" style={{ color: 'var(--gg-emerald)' }}>{fmtMoney(fin.paid)}</td>
                      <td className="gg-tabular font-semibold" style={{ color: fin.left > 0 ? 'var(--gg-danger)' : 'var(--gg-text-dim)' }}>{fmtMoney(fin.left)}</td>
                      <td><span className={`gg-chip gg-chip-${PAY_STATUS_TONE[fin.status]}`}>{fin.status}</span></td>
                      <td>
                        <div className="flex gap-1 justify-end">
                          <button onClick={() => openPay(r)} className="gg-btn-ghost" style={{ color: 'var(--gg-emerald)' }} title="Payments" data-testid={`pay-${r.id}`}><Coins size={13} /> Pay</button>
                          <button onClick={() => removeShipment(r.id)} className="gg-btn-ghost" style={{ color: 'var(--gg-danger)' }} data-testid={`del-shipment-${r.id}`}><Trash2 size={13} /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add shipment modal */}
      {shipOpen && (
        <div className="gg-cmdk-overlay flex items-start justify-center pt-16 overflow-y-auto" onClick={() => !shipSaving && setShipOpen(false)}>
          <motion.div initial={{ opacity: 0, y: 10, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} onClick={(e) => e.stopPropagation()} className="gg-cmdk-panel p-6 mb-10" style={{ width: 'min(560px, 94vw)', maxHeight: '86vh', overflowY: 'auto' }} data-testid="add-shipment-form">
            <p className="gg-eyebrow" style={{ color: 'var(--gg-accent)' }}>{customer.name}</p>
            <h2 className="gg-hero-title" style={{ fontSize: 22 }}>Add Shipment</h2>
            <div className="mt-5 grid grid-cols-2 gap-3">
              <F label="Type"><select value={shipForm.category} onChange={(e) => setShipForm({ ...shipForm, category: e.target.value })} className="gg-input" data-testid="ship-category">{CATEGORIES.map((c) => <option key={c.v} value={c.v}>{c.label}</option>)}</select></F>
              <F label="Tracking / Reference"><input value={shipForm.trackingNumber} onChange={(e) => setShipForm({ ...shipForm, trackingNumber: e.target.value })} className="gg-input" data-testid="ship-tracking" /></F>
              <F label="Goods / description"><input value={shipForm.description} onChange={(e) => setShipForm({ ...shipForm, description: e.target.value })} className="gg-input" data-testid="ship-desc" /></F>
              <F label="Month"><select value={shipForm.month} onChange={(e) => setShipForm({ ...shipForm, month: e.target.value })} className="gg-input" data-testid="ship-month"><option value="">— Month —</option>{MONTHS.map((m) => <option key={m} value={m}>{m}</option>)}</select></F>
              <F label="Cost — we pay (₪)"><input type="number" value={shipForm.shippingCost} onChange={(e) => setShipForm({ ...shipForm, shippingCost: e.target.value })} className="gg-input" data-testid="ship-cost" /></F>
              <F label="Amount requested (₪)"><input type="number" value={shipForm.amountRequested} onChange={(e) => setShipForm({ ...shipForm, amountRequested: e.target.value })} className="gg-input" data-testid="ship-requested" /></F>
              <div className="col-span-2"><F label="Notes"><textarea value={shipForm.notes} onChange={(e) => setShipForm({ ...shipForm, notes: e.target.value })} className="gg-input" rows={2} style={{ height: 'auto', padding: '10px 14px' }} data-testid="ship-notes" /></F></div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setShipOpen(false)} disabled={shipSaving} className="gg-btn-secondary" data-testid="ship-cancel">Cancel</button>
              <button onClick={addShipment} disabled={shipSaving} className="gg-btn-primary" data-testid="ship-save">{shipSaving ? 'Saving…' : 'Add Shipment'}</button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Payments modal */}
      {payModal && (
        <div className="gg-cmdk-overlay flex items-start justify-center pt-16 overflow-y-auto" onClick={() => setPayModal(null)}>
          <motion.div initial={{ opacity: 0, y: 10, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} onClick={(e) => e.stopPropagation()} className="gg-cmdk-panel p-6 mb-10" style={{ width: 'min(540px, 94vw)', maxHeight: '86vh', overflowY: 'auto' }} data-testid="cust-pay-modal">
            <p className="gg-eyebrow" style={{ color: 'var(--gg-accent)' }}>Payments</p>
            <h2 className="gg-hero-title" style={{ fontSize: 20 }}>{payModal.record.trackingNumber || payModal.record.description || 'Shipment'}</h2>
            <div className="grid grid-cols-3 gap-2 my-4">
              <div className="rounded-xl p-3 text-center" style={{ background: 'var(--gg-surface-2)' }}><p className="gg-eyebrow">Requested</p><p className="gg-tabular font-bold" style={{ fontSize: 18 }}>{fmtMoney(payModal.amountRequested)}</p></div>
              <div className="rounded-xl p-3 text-center" style={{ background: 'var(--gg-emerald-soft)' }}><p className="gg-eyebrow" style={{ color: 'var(--gg-emerald)' }}>Paid</p><p className="gg-tabular font-bold" style={{ fontSize: 18, color: 'var(--gg-emerald)' }}>{fmtMoney(payModal.amountPaid)}</p></div>
              <div className="rounded-xl p-3 text-center" style={{ background: (payModal.balanceLeft || 0) > 0 ? 'rgba(255,59,48,0.1)' : 'var(--gg-emerald-soft)' }}><p className="gg-eyebrow" style={{ color: (payModal.balanceLeft || 0) > 0 ? 'var(--gg-danger)' : 'var(--gg-emerald)' }}>Left</p><p className="gg-tabular font-bold" style={{ fontSize: 18, color: (payModal.balanceLeft || 0) > 0 ? 'var(--gg-danger)' : 'var(--gg-emerald)' }}>{fmtMoney(payModal.balanceLeft)}</p></div>
            </div>
            <div className="rounded-2xl p-4 mb-4" style={{ background: 'var(--gg-surface-2)' }}>
              <p className="gg-eyebrow mb-2">Add payment</p>
              <div className="grid grid-cols-2 gap-2">
                <input type="number" placeholder="Amount ₪" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} className="gg-input" data-testid="cust-pay-amount" />
                <input type="date" value={payForm.date} onChange={(e) => setPayForm({ ...payForm, date: e.target.value })} className="gg-input" data-testid="cust-pay-date" />
                <select value={payForm.method} onChange={(e) => setPayForm({ ...payForm, method: e.target.value })} className="gg-input" data-testid="cust-pay-method">{PAYMENT_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}</select>
                <input placeholder="Note" value={payForm.note} onChange={(e) => setPayForm({ ...payForm, note: e.target.value })} className="gg-input" data-testid="cust-pay-note" />
              </div>
              <button onClick={addPayment} disabled={payBusy} className="gg-btn-primary w-full justify-center mt-3" data-testid="cust-pay-add"><Plus size={14} /> {payBusy ? 'Adding…' : 'Add payment'}</button>
            </div>
            {(payModal.payments || []).length === 0 ? (
              <p className="text-sm py-2 text-center" style={{ color: 'var(--gg-text-dim)' }}>No payments yet.</p>
            ) : (
              <div className="space-y-1">
                {payModal.payments.map((p) => (
                  <div key={p.id} className="flex items-center justify-between text-sm py-2 px-1" style={{ borderTop: '1px solid var(--gg-border)' }}>
                    <span style={{ color: 'var(--gg-text-secondary)' }}>{p.date} · {p.method}{p.note ? ` · ${p.note}` : ''}</span>
                    <span className="flex items-center gap-2"><span className="gg-tabular font-semibold" style={{ color: 'var(--gg-emerald)' }}>{fmtMoney(p.amount)}</span><button onClick={() => deletePayment(p.id)} className="gg-btn-ghost" style={{ color: 'var(--gg-danger)' }} data-testid={`cust-pay-del-${p.id}`}><Trash2 size={12} /></button></span>
                  </div>
                ))}
              </div>
            )}
            <div className="flex justify-end mt-6"><button onClick={() => setPayModal(null)} className="gg-btn-secondary" data-testid="cust-pay-close">Close</button></div>
          </motion.div>
        </div>
      )}

      {/* Ledger add-entry modal */}
      {ledgerModal && (
        <div className="gg-cmdk-overlay flex items-start justify-center pt-24 overflow-y-auto" onClick={() => !ledgerBusy && setLedgerModal(null)}>
          <motion.div initial={{ opacity: 0, y: 10, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} onClick={(e) => e.stopPropagation()} className="gg-cmdk-panel p-6" style={{ width: 'min(480px, 94vw)', maxHeight: '86vh', overflowY: 'auto' }} data-testid="ledger-modal">
            <p className="gg-eyebrow" style={{ color: ledgerModal === 'charge' ? 'var(--gg-warning, #FF9500)' : 'var(--gg-emerald)' }}>{ledgerModal === 'charge' ? 'New charge' : 'New payment'}</p>
            <h2 className="gg-hero-title" style={{ fontSize: 22 }}>{ledgerModal === 'charge' ? `I need money from ${customer.name}` : `${customer.name} paid`}</h2>
            <div className="mt-5 grid grid-cols-2 gap-3">
              <F label="Amount (₪) *"><input type="number" value={ledgerForm.amount} onChange={(e) => setLedgerForm({ ...ledgerForm, amount: e.target.value })} className="gg-input" data-testid="ledger-amount" autoFocus /></F>
              <F label="Date"><input type="date" value={ledgerForm.date} onChange={(e) => setLedgerForm({ ...ledgerForm, date: e.target.value })} className="gg-input" data-testid="ledger-date" /></F>
              {ledgerModal === 'payment' && (
                <F label="Paid by"><select value={ledgerForm.method} onChange={(e) => setLedgerForm({ ...ledgerForm, method: e.target.value })} className="gg-input" data-testid="ledger-method">{PAYMENT_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}</select></F>
              )}
              <div className={ledgerModal === 'payment' ? '' : 'col-span-2'}>
                <F label="Note"><input value={ledgerForm.note} onChange={(e) => setLedgerForm({ ...ledgerForm, note: e.target.value })} className="gg-input" placeholder={ledgerModal === 'charge' ? 'e.g. clearance fee' : 'optional'} data-testid="ledger-note" /></F>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setLedgerModal(null)} disabled={ledgerBusy} className="gg-btn-secondary" data-testid="ledger-cancel">Cancel</button>
              <button onClick={saveLedger} disabled={ledgerBusy} className="gg-btn-primary" data-testid="ledger-save">{ledgerBusy ? 'Saving…' : 'Save'}</button>
            </div>
          </motion.div>
        </div>
      )}
    </ErpShell>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'var(--gg-surface-2)', color: 'var(--gg-text-dim)' }}><Icon size={14} /></span>
      <div className="min-w-0"><p className="gg-eyebrow" style={{ fontSize: 9 }}>{label}</p><p className="text-sm mt-0.5 break-words" style={{ color: 'var(--gg-text)' }}>{value}</p></div>
    </div>
  );
}

const F = ({ label, children }) => (
  <div><label className="gg-eyebrow block mb-1">{label}</label>{children}</div>
);
